import { ListConfigurationRecordType, UserListConfigurationRecordType } from "pages/SharedRecords.types";
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDataProvider, useListContext, useNotify, useStore } from "react-admin";

type ListConfigurationContextType = {
  configurations: ListConfigurationRecordType[];
  currentConfig: ListConfigurationRecordType | null;
  initialized: boolean;
  loading: boolean;
  loadConfigurations: (storeKey: string) => Promise<void>;
  saveConfiguration: (config: Omit<ListConfigurationRecordType, "id">) => Promise<ListConfigurationRecordType>;
  updateConfiguration: (id: number, config: Partial<ListConfigurationRecordType>) => Promise<void>;
  deleteConfiguration: (id: number) => Promise<void>;
  selectConfiguration: (config: ListConfigurationRecordType | null) => void;
};

const ListConfigurationContext = createContext<ListConfigurationContextType | undefined>(undefined);

/**
 * A saved view stores what it hides, so a column added later (e.g. a new custom field)
 * would show up in it. Its column order lists every column known when it was saved, so
 * anything outside that order is treated as hidden too.
 */
export function getSavedHiddenColumns(config: ListConfigurationRecordType, allColumns: readonly string[]): string[] {
  const hidden = Array.isArray(config.hiddenColumns) ? config.hiddenColumns : [];
  const order = Array.isArray(config.columnOrder) ? config.columnOrder : [];
  if (order.length === 0) return hidden;
  return [...hidden, ...allColumns.filter((col) => !order.includes(col) && !hidden.includes(col))];
}

function mapBackendToFrontend(data: UserListConfigurationRecordType): ListConfigurationRecordType {
  return {
    id: data.id,
    name: data.name,
    storeKey: data.resource,
    filterValues: data.filter_values && typeof data.filter_values === "object" ? data.filter_values : {},
    hiddenColumns: Array.isArray(data.hidden_columns) ? data.hidden_columns : [],
    columnOrder: Array.isArray(data.column_order) ? data.column_order : [],
    sortField: data.sort_field,
    sortOrder: data.sort_order,
    isFullscreen: data.is_fullscreen ?? false,
  };
}

function mapFrontendToBackend(
  data: Partial<ListConfigurationRecordType>,
): Partial<Omit<UserListConfigurationRecordType, "id">> {
  return {
    ...(data.name !== undefined && { name: data.name }),
    ...(data.storeKey !== undefined && { resource: data.storeKey }),
    ...(data.filterValues !== undefined && { filter_values: data.filterValues }),
    ...(data.hiddenColumns !== undefined && { hidden_columns: data.hiddenColumns }),
    ...(data.columnOrder !== undefined && { column_order: data.columnOrder }),
    ...(data.sortField !== undefined && { sort_field: data.sortField }),
    ...(data.sortOrder !== undefined && { sort_order: data.sortOrder }),
    ...(data.isFullscreen !== undefined && { is_fullscreen: data.isFullscreen }),
  };
}

const LAST_CONFIG_KEY_PREFIX = "lastListConfig_";

function getLastSelectedConfigId(resource: string): number | null {
  const stored = localStorage.getItem(`${LAST_CONFIG_KEY_PREFIX}${resource}`);
  return stored ? parseInt(stored, 10) : null;
}

function setLastSelectedConfigId(resource: string, configId: number | null): void {
  if (configId === null) {
    localStorage.removeItem(`${LAST_CONFIG_KEY_PREFIX}${resource}`);
  } else {
    localStorage.setItem(`${LAST_CONFIG_KEY_PREFIX}${resource}`, configId.toString());
  }
}

export function ListConfigurationProvider({ children }: { children: ReactNode }) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [configurations, setConfigurations] = useState<ListConfigurationRecordType[]>([]);
  const [currentConfig, setCurrentConfig] = useState<ListConfigurationRecordType | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadedResourceRef = useRef<string | null>(null);

  const loadConfigurations = useCallback(
    async (storeKey: string) => {
      if (loadedResourceRef.current === storeKey) {
        return;
      }

      setLoading(true);
      try {
        const { data } = await dataProvider.getList("user_list_configurations", {
          filter: { resource: storeKey },
          pagination: { page: 1, perPage: 100 },
          sort: { field: "name", order: "ASC" },
        });

        const mappedData = data.map(mapBackendToFrontend);
        setConfigurations(mappedData);
        loadedResourceRef.current = storeKey;

        const lastConfigId = getLastSelectedConfigId(storeKey);
        if (lastConfigId) {
          const lastConfig = mappedData.find((c: ListConfigurationRecordType) => c.id === lastConfigId);
          if (lastConfig) {
            setCurrentConfig(lastConfig);
          }
        }
      } catch {
        notify("resources.user_list_configurations.hints.error_loading_configuration", { type: "error" });
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    },
    [dataProvider, notify],
  );

  const saveConfiguration = useCallback(
    async (config: Omit<ListConfigurationRecordType, "id">) => {
      try {
        const { data } = await dataProvider.create<UserListConfigurationRecordType>("user_list_configurations", {
          data: mapFrontendToBackend(config),
        });

        const mappedData = mapBackendToFrontend(data);
        setConfigurations((prev) => [...prev, mappedData]);
        setCurrentConfig(mappedData);
        setLastSelectedConfigId(config.storeKey, mappedData.id);
        notify("ra.notification.created", { type: "info", messageArgs: { smart_count: 1 } });
        return mappedData;
      } catch (error) {
        notify("ra.notification.http_error", { type: "error" });
        throw error;
      }
    },
    [dataProvider, notify],
  );

  const updateConfiguration = useCallback(
    async (id: number, updates: Partial<ListConfigurationRecordType>) => {
      try {
        const { data } = await dataProvider.update<UserListConfigurationRecordType>("user_list_configurations", {
          id,
          data: mapFrontendToBackend(updates),
          previousData: { id },
        });

        const mappedData = mapBackendToFrontend(data);
        setConfigurations((prev) => prev.map((c) => (c.id === id ? mappedData : c)));
        setCurrentConfig((prev) => (prev?.id === id ? mappedData : prev));
        notify("ra.notification.updated", { type: "info", messageArgs: { smart_count: 1 } });
      } catch (error) {
        notify("ra.notification.http_error", { type: "error" });
        throw error;
      }
    },
    [dataProvider, notify],
  );

  const deleteConfiguration = useCallback(
    async (id: number) => {
      try {
        await dataProvider.delete("user_list_configurations", { id });

        setConfigurations((prev) => prev.filter((c) => c.id !== id));
        setCurrentConfig((prev) => {
          if (prev?.id === id) {
            if (loadedResourceRef.current) {
              setLastSelectedConfigId(loadedResourceRef.current, null);
            }
            return null;
          }
          return prev;
        });
        notify("ra.notification.deleted", { type: "info", messageArgs: { smart_count: 1 } });
      } catch (error) {
        notify("ra.notification.http_error", { type: "error" });
        throw error;
      }
    },
    [dataProvider, notify],
  );

  const selectConfiguration = useCallback((config: ListConfigurationRecordType | null) => {
    setCurrentConfig(config);
    if (config) {
      setLastSelectedConfigId(config.storeKey, config.id);
    } else if (loadedResourceRef.current) {
      setLastSelectedConfigId(loadedResourceRef.current, null);
    }
  }, []);

  return (
    <ListConfigurationContext.Provider
      value={{
        configurations,
        currentConfig,
        initialized,
        loading,
        loadConfigurations,
        saveConfiguration,
        updateConfiguration,
        deleteConfiguration,
        selectConfiguration,
      }}
    >
      {children}
    </ListConfigurationContext.Provider>
  );
}

export function useListConfiguration() {
  const context = useContext(ListConfigurationContext);
  if (!context) {
    throw new Error("useListConfiguration must be used within ListConfigurationProvider");
  }
  return context;
}

type UseConfigurableListOptions = {
  storeKey: string;
  defaultFilterValues?: Record<string, unknown>;
  defaultSort?: { field: string; order: "ASC" | "DESC" };
};

export function useConfigurableList(options: UseConfigurableListOptions) {
  const { storeKey, defaultFilterValues = {}, defaultSort = { field: "id", order: "ASC" } } = options;
  const { currentConfig, initialized, loading, loadConfigurations, ...rest } = useListConfiguration();

  useEffect(() => {
    loadConfigurations(storeKey);
  }, [loadConfigurations, storeKey]);

  const filterDefaultValues = currentConfig?.filterValues ?? defaultFilterValues;
  const sort =
    currentConfig?.sortField && currentConfig?.sortOrder
      ? { field: currentConfig.sortField, order: currentConfig.sortOrder }
      : defaultSort;

  return {
    initialized,
    loading,
    currentConfig,
    filterDefaultValues,
    sort,
    ...rest,
  };
}

type UseListConfigurationApplierOptions = {
  storeKey: string;
  defaultFilterValues?: Record<string, unknown>;
  defaultSort?: { field: string; order: "ASC" | "DESC" };
  allColumns?: readonly string[];
  defaultColumns?: string[];
};

export function useListConfigurationApplier(options: UseListConfigurationApplierOptions) {
  const {
    storeKey,
    allColumns,
    defaultColumns,
    defaultFilterValues = {},
    defaultSort = { field: "name", order: "ASC" },
  } = options;
  const { currentConfig, initialized } = useListConfiguration();
  const { setFilters, setSort } = useListContext();

  const [previousKey, setPreviousKey] = useStore<string>(`${storeKey}.appliedConfigKey`, "");
  const [appliedConfigId, setAppliedConfigId] = useStore<number | null | undefined>(`${storeKey}.appliedConfigId`);

  const [, setHiddenColumns] = useStore<string[]>(`${storeKey}.datatable`, []);
  const [, setColumnRanks] = useStore<number[]>(`${storeKey}.datatable_columnRanks`, []);
  const [, setIsFullscreen] = useStore<boolean>(`${storeKey}.fullscreen`, false);

  const [pendingSearch, setPendingSearch] = useStore<string>(`${storeKey}.pendingSearch`, "");

  const configId = currentConfig?.id ?? null;
  const allColumnsKey = allColumns?.join(",") ?? "";

  useEffect(() => {
    const currentKey = `${configId}_${allColumnsKey}`;
    if (previousKey === currentKey) return;
    // Columns of custom fields arrive late; re-applying filters then would wipe a search applied in the meantime.
    const configChanged = appliedConfigId !== configId;
    setPreviousKey(currentKey);
    setAppliedConfigId(configId);

    if (currentConfig) {
      if (configChanged && currentConfig.filterValues) {
        setFilters(currentConfig.filterValues, [], false);
      }
      if (configChanged && currentConfig.sortField && currentConfig.sortOrder) {
        setSort({ field: currentConfig.sortField, order: currentConfig.sortOrder });
      }

      if (allColumns) {
        setHiddenColumns(getSavedHiddenColumns(currentConfig, allColumns));

        const order = Array.isArray(currentConfig.columnOrder) ? currentConfig.columnOrder : [];
        if (order.length > 0) {
          let nextRank = order.length;
          const ranks = allColumns.map((col) => {
            const orderIndex = order.indexOf(col);
            return orderIndex !== -1 ? orderIndex : nextRank++;
          });
          setColumnRanks(ranks);
        } else {
          setColumnRanks(allColumns.map((_, i) => i));
        }
      }
      setIsFullscreen(currentConfig.isFullscreen ?? false);
    } else {
      if (configChanged) {
        setFilters(defaultFilterValues, [], false);
        setSort(defaultSort);
      }

      if (allColumns && defaultColumns) {
        setHiddenColumns(allColumns.filter((col) => !defaultColumns.includes(col)));
        setColumnRanks(allColumns.map((_, i) => i));
      }
    }
  }, [configId, allColumnsKey]);

  useEffect(() => {
    if (!pendingSearch || !initialized) return;
    const baseFilters = currentConfig?.filterValues ?? defaultFilterValues;
    setFilters({ ...baseFilters, search: pendingSearch }, [], false);
    setPendingSearch("");
  }, [pendingSearch, initialized]);
}
